import { currentDivisionId } from 'common/src/stores/useFiltersStore';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
  Switch,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@nextui-org/react';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { supabase } from 'common/src/supabase';
import SeasonFilter from '../../../../common/src/components/filters/SeasonFilter';
import { PreSettlementsTable } from '../../components/tables/pre-settlements-table';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useUserStore from '../../../../common/src/stores/useUserStore';
import { getMovementsFromFrutic } from 'common/src/api/frutic';
import { Settlement, ShipmentBox } from 'common/src/utils/types';
import {
  buildSettlementQuadratureProductKey,
  buildShipmentQuadratureProductKey,
} from 'common/src/utils/quadrature';
import { getSettlementsByExporterId } from 'common/src/api/settlements';
import {
  savePreSettlementSettings,
  loadPreSettlementSettings,
  PreSettlementSettings,
  PreSettlementCalculationCriteria,
} from 'common/src/api/presettlement-settings';
import { getWeekKey } from 'common/src/utils/functions';
import {
  buildPoolProductKey,
  normalizePresettlementProductDefinition,
} from 'common/src/utils/presettlement-product-key';
import { logUserAction } from '../../services/actionsService';
import {
  demoPreSettlementsShipmentBoxes,
  demoPreSettlementsValidSettlements,
  demoPreSettlementsMovements,
} from '../../utils/data/demo-pre-settlements';
import { longToShortSeason } from '../../utils/functions';
import * as XLSX from 'xlsx';
import useDataStore from '../../../../common/src/stores/useDataStore';
import {
  SPECIES_ID_TO_FRUTIC_SPECIES,
  VALID_FORMATS,
} from 'common/src/utils/definitions';
import { exporterUsesFrutic } from 'common/src/utils/frutic-functions';
import { Icon } from '@iconify/react';
import Header from '../../components/layouts/Header';
import { Alert, LoadingState, PageLayout } from '../../components/ui';
import { BoxFormatMappingSection } from '../../components/frutic-box-mapping/FruticBoxMapping';
import useFruticStore from '../../store/useFruticStore';
import { ReceiverMappingSection } from '../../components/receiver-mapping/ReceiverMapping';
import { getShipmentsAllSources } from '../../services/shipmentsService';
import CustomAutocomplete from '../../components/common/CustomAutocomplete';
import GrowerExceptionsEditor from '../../components/pre-settlements/GrowerExceptionsEditor';
import {
  createGrowerChargeResolver,
  parseGrowerExceptions,
  type GrowerException,
} from 'common/src/utils/presettlement-grower-exceptions';
import TariffEditor from '../../components/pre-settlements/TariffEditor';
import { calculationSettingsSnapshot } from '../../utils/presettlement-settings-snapshot';
import {
  legacyTariffConfiguration,
  createAppliedTariffResolver,
  tariffDimensions,
  TARIFF_WEEK_LABELS,
  type TariffConfiguration,
} from 'common/src/utils/presettlement-tariffs';

interface ProcessedData {
  [variety: string]: {
    [caliber: string]: {
      value: number;
      kilos: number;
      settledKilos: number;
      pricedSettledKilos: number;
      realSettledKilos: number;
      realValue: number;
    };
  };
}

interface ProcessedProducerData {
  [producer: string]: {
    [variety: string]: {
      [caliber: string]: {
        value: number;
        kilos: number;
        settledKilos: number;
        pricedSettledKilos: number;
        realSettledKilos: number;
        realValue: number;
      };
    };
  };
}

type PoolCriteria = PreSettlementCalculationCriteria;

// Add new interface for movements
interface Movement {
  amount_usd: number;
  producer_name: string;
}

const normalizeToken = (value: string | number | null | undefined) =>
  (value ?? '').toString().trim().toUpperCase();

type ValidBoxFormat = (typeof VALID_FORMATS)[number];

const isValidBoxFormat = (
  value: string | null | undefined
): value is ValidBoxFormat =>
  VALID_FORMATS.includes(normalizeToken(value) as ValidBoxFormat);

const normalizeBoxCode = (value: string | null | undefined) =>
  (value || '').trim().toUpperCase();

const hasValidProductKeyFormat = (key: string) =>
  isValidBoxFormat(key.split('|')[3]);

const getPoolGroupKey = (box: any, poolBy: PoolCriteria) => {
  if (poolBy === 'reference') {
    return box.reference_number ?? 'SIN_REFERENCIA';
  }
  if (poolBy === 'ship') {
    return box.ship_trip_id ?? 'SIN_NAVE';
  }
  if (poolBy === 'reception') {
    return getWeekKey(box.reception_date) ?? 'SIN_SEMANA';
  }
  if (poolBy === 'process') {
    return getWeekKey(box.process_date) ?? 'SIN_SEMANA';
  }
  if (poolBy === 'packing') {
    return (
      getWeekKey(box.packing_etd ?? box.packing_date ?? box.ship_etd) ??
      'SIN_SEMANA'
    );
  }
  if (poolBy === 'etd') {
    return box.week_etd ?? getWeekKey(box.ship_etd) ?? 'SIN_SEMANA';
  }
  if (poolBy === 'eta') {
    return box.week_eta ?? getWeekKey(box.ship_eta) ?? 'SIN_SEMANA';
  }
  return 'SIN_POOL';
};

const calculateFOBValuesPreSettlementsPool = (
  shipmentBoxes: ShipmentBox[],
  settlements: Settlement[],
  poolBy: PoolCriteria,
  poolProductDefinition: string[],
  considerExtraRevenue = false
): ShipmentBox[] => {
  const referenceTotals = new Map<
    string,
    Map<string, { totalFob: number; totalKilos: number }>
  >();

  settlements.forEach((settlement) => {
    if (!settlement.reference_number) return;
    const reference = settlement.reference_number;
    const quadratureContext = {
      season: settlement.season,
      speciesId: settlement.species_id,
    };
    const detailTotals =
      referenceTotals.get(reference) ||
      new Map<string, { totalFob: number; totalKilos: number }>();

    const exchangeRate = settlement.exchange_rate || 1;

    settlement.settlement_details.forEach((detail) => {
      const productKey = buildSettlementQuadratureProductKey(
        detail,
        quadratureContext
      );
      const current = detailTotals.get(productKey) || {
        totalFob: 0,
        totalKilos: 0,
      };
      const detailKilos = Number(detail.kilos || 0);
      // Sum per-detail revenues using their own amount_usd (avoids cross-converting with parent exchange rate)
      const extraRevenueUsd =
        considerExtraRevenue && detail.settlement_detail_revenues?.length
          ? detail.settlement_detail_revenues.reduce(
              (sum, r) => sum + (r.amount_usd || 0),
              0
            )
          : 0;
      current.totalFob += Number(detail.usd_total_fob || 0) + extraRevenueUsd;
      current.totalKilos += detailKilos;
      detailTotals.set(productKey, current);
    });

    referenceTotals.set(reference, detailTotals);
  });

  const poolTotals = new Map<
    string,
    { totalKilos: number; refKilos: number; refValue: number }
  >();

  const stagedBoxes = shipmentBoxes.map((box) => {
    const reference = box.reference_number;
    const referenceProducts = referenceTotals.get(reference);
    const referenceProductKey = buildShipmentQuadratureProductKey(box, {
      season: box.season,
      speciesId: box.species_id,
    });
    const referenceTotalsForProduct =
      referenceProducts?.get(referenceProductKey);
    const referenceFobPerKg =
      referenceTotalsForProduct && referenceTotalsForProduct.totalKilos > 0
        ? referenceTotalsForProduct.totalFob /
          referenceTotalsForProduct.totalKilos
        : null;

    const poolProductKey = buildPoolProductKey(box, poolProductDefinition);
    const poolGroupKey = getPoolGroupKey(box, poolBy);
    const poolKey = `${poolGroupKey}::${poolProductKey}`;

    const unitWeight = Number(box.box_weight ?? box.unit_weight ?? 0);
    const quantity = Number(box.quantity || 0);
    const kilos = unitWeight * quantity;

    const currentPool = poolTotals.get(poolKey) || {
      totalKilos: 0,
      refKilos: 0,
      refValue: 0,
    };

    currentPool.totalKilos += kilos;
    if (referenceFobPerKg != null) {
      currentPool.refKilos += kilos;
      currentPool.refValue += referenceFobPerKg * kilos;
    }
    poolTotals.set(poolKey, currentPool);

    return {
      box,
      referenceFobPerKg,
      poolKey,
    };
  });

  const poolValues = new Map<
    string,
    { poolFobPerKg: number | null; poolLiquidated: number }
  >();

  poolTotals.forEach((totals, poolKey) => {
    const poolLiquidated =
      totals.totalKilos > 0 ? totals.refKilos / totals.totalKilos : 0;
    const poolFobPerKg =
      totals.refKilos > 0 ? totals.refValue / totals.refKilos : null;
    poolValues.set(poolKey, { poolFobPerKg, poolLiquidated });
  });

  return stagedBoxes.map(({ box, referenceFobPerKg, poolKey }) => {
    const poolValue = poolValues.get(poolKey);
    const poolFobPerKg = poolValue?.poolFobPerKg ?? null;
    const poolLiquidated = poolValue?.poolLiquidated ?? 0;
    const fobValue = referenceFobPerKg ?? poolFobPerKg ?? null;
    const source =
      referenceFobPerKg != null
        ? 'reference'
        : poolFobPerKg != null
          ? 'pool'
          : null;

    return {
      ...box,
      fob_value_per_kg: fobValue,
      reference_fob_value_per_kg: referenceFobPerKg,
      pool_fob_value_per_kg: poolFobPerKg,
      fob_value_per_kg_source: source,
      liquidated_percentage: poolLiquidated,
    };
  });
};

const PreSettlementsPageNew = () => {
  const { t } = useTranslation('preSettlements');
  const { userProfile, session } = useUserStore();
  const order = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
  const { mappedFormats, fetchedMappedAndUnmappedFormats } = useFruticStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const [selectedProducer, setSelectedProducer] = useState<string | null>(null);
  const [selectedProducerGroup, setSelectedProducerGroup] = useState<
    string | null
  >(null);
  const [selectedVariety, setSelectedVariety] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<PoolCriteria | null>(null);
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [unmappedShipmentBoxes, setUnmappedShipmentBoxes] = useState<
    ShipmentBox[]
  >([]);
  // Derive mapped rows in the same render as the data/loading update. A
  // separate effect briefly validated populated settlements against zero rows.
  const rawShipmentBoxes = useMemo<ShipmentBox[]>(() => {
    if (!fetchedMappedAndUnmappedFormats) return [];
    return unmappedShipmentBoxes.map((box) => {
      const mapping = mappedFormats.find(
        (m) => normalizeBoxCode(m.frutic_code) === normalizeBoxCode(box.box_type)
      );
      const box_format =
        mapping?.cherry_box_format ||
        (isValidBoxFormat(box.box_format)
          ? normalizeToken(box.box_format)
          : 'NO ENCONTRADO');
      const unit_weight = box.box_weight;
      return {
        ...box,
        box_format,
        unit_weight,
        complete_format: `${box_format}-${unit_weight}KG`,
      };
    });
  }, [unmappedShipmentBoxes, mappedFormats, fetchedMappedAndUnmappedFormats]);
  const [shipmentBoxes, setShipmentBoxes] = useState<ShipmentBox[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoadError, setDataLoadError] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [viewMode, setViewMode] = useState<'variety' | 'producer'>('producer');
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [percentageTake, setPercentageTake] = useState<number>(8);
  // Texto del campo de comisión: se guarda aparte para poder escribir
  // decimales como 8.05 sin que el número los reescriba al teclear.
  const [percentageText, setPercentageText] = useState('8');
  const [costsPerKg, setCostsPerKg] = useState<{ [format: string]: number }>(
    {}
  );
  const [movements, setMovements] = useState<Movement[]>([]);

  const [costType, setCostType] = useState<'sameCost' | 'formatCost'>(
    'sameCost'
  );
  const [sectionsVisible, setSectionsVisible] = useState(true);
  const [productDefinition, setProductDefinition] = useState<string[]>([
    'variety',
    'caliber',
    'color',
    'box_format',
    'box_label',
  ]);
  const [withReceptionDate, setWithReceptionDate] = useState(true);
  const [withProcessDate, setWithProcessDate] = useState(true);

  // Settings management
  const [savingSettings, setSavingSettings] = useState(false);
  const [tariffsValid, setTariffsValid] = useState(true);
  const [growerExceptions, setGrowerExceptions] = useState<GrowerException[]>(
    []
  );
  const growerExceptionsValid =
    parseGrowerExceptions(growerExceptions) !== null;
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [hasSettings, setHasSettings] = useState(false);
  // Temporada de la que se heredó la configuración (null si es propia).
  const [settingsInheritedFrom, setSettingsInheritedFrom] = useState<
    string | null
  >(null);
  const [bypassQuadrature, setBypassQuadrature] = useState(false);
  const [considerExtraRevenue, setConsiderExtraRevenue] = useState(true);
  const {
    isOpen: isSaveModalOpen,
    onOpen: onSaveModalOpen,
    onOpenChange: onSaveModalOpenChange,
  } = useDisclosure();

  const [savedTariffs, setSavedTariffs] = useState<TariffConfiguration | null>(
    null
  );
  const [settingsBaseline, setSettingsBaseline] = useState<{
    scope: string;
    snapshot: string;
  } | null>(null);
  const [settingsSaveError, setSettingsSaveError] = useState(false);
  const [settingsLoadError, setSettingsLoadError] = useState(false);
  const settingsContext = JSON.stringify([
    userProfile?.exporter_id,
    selectedSeason,
    selectedSpecies?.id,
    session?.user.id,
  ]);
  const settingsRequest = useRef(0);
  const settingsScope = useRef<string | null>(null);
  const tariffConfig = useMemo(
    () => savedTariffs ?? legacyTariffConfiguration(costsPerKg),
    [savedTariffs, costsPerKg]
  );
  const currentSettingsSnapshot = calculationSettingsSnapshot({
    calculation_criteria: groupBy,
    product_definition: productDefinition,
    cost_type: costType,
    commission_percentage: percentageTake,
    consider_extra_revenue: considerExtraRevenue,
    tariff_configuration: tariffConfig,
    grower_exceptions: growerExceptions,
  });
  const settingsReady =
    settingsBaseline?.scope === settingsContext &&
    !loadingSettings &&
    !settingsLoadError;
  const hasCalculationChanges =
    settingsBaseline?.scope === settingsContext &&
    settingsBaseline.snapshot !== currentSettingsSnapshot;
  const appliedTariffFor = useMemo(
    () => createAppliedTariffResolver(rawShipmentBoxes, tariffConfig, costType),
    [rawShipmentBoxes, tariffConfig, costType]
  );
  const growerChargesFor = useMemo(
    () => createGrowerChargeResolver(growerExceptions, percentageTake),
    [growerExceptions, percentageTake]
  );
  const exceptionGrowers = useMemo(
    () => [
      ...new Set(
        rawShipmentBoxes
          .map((box) => box.producer_name)
          .filter((name): name is string => !!name)
      ),
    ],
    [rawShipmentBoxes]
  );
  const tariffApplicationLabel =
    costType === 'sameCost' ? t('Misma Tarifa') : t('Separar Tarifa');
  const tariffSummary = [
    tariffConfig.packing === 'packing'
      ? t('Por Packing')
      : t('Packing: General'),
    tariffConfig.packaging === 'format'
      ? t('Por Formato')
      : t('Por Código Embalaje'),
    tariffConfig.week === 'general'
      ? t('Semana: General')
      : t(TARIFF_WEEK_LABELS[tariffConfig.week]),
  ].join(' · ');

  const poolAvailability = useMemo(() => {
    const total = rawShipmentBoxes.length;
    const availabilityFor = (hasDate: (box: ShipmentBox) => boolean) => {
      if (!total) {
        return { disabled: true, reason: 'Sin despachos' };
      }
      const missing = rawShipmentBoxes.reduce(
        (count, box) => count + (hasDate(box) ? 0 : 1),
        0
      );
      if (missing > 0) {
        return {
          disabled: true,
          reason: `Faltan fechas en ${missing} de ${total} filas`,
        };
      }
      return { disabled: false, reason: '' };
    };

    return {
      reception: availabilityFor((box) => Boolean(box.reception_date)),
      process: availabilityFor((box) => Boolean(box.process_date)),
      packing: availabilityFor((box) =>
        Boolean(box.packing_etd ?? box.packing_date ?? box.ship_etd)
      ),
      etd: availabilityFor((box) => Boolean(box.ship_etd)),
      eta: availabilityFor((box) => Boolean(box.ship_eta)),
    };
  }, [rawShipmentBoxes]);

  const fetchUnmappedData = useCallback(async (isCurrentRequest: () => boolean = () => true) => {
    try {
      const exporter = userProfile?.exporter;
      const hasFrutic = exporterUsesFrutic(exporter, selectedSeason);
      const hasDemo = exporter?.hasDemo;

      const [unmappedShipmentBoxes, movementsResponse, settlementsResponse] =
        await Promise.all([
          getShipmentsAllSources({
            userProfile,
            session,
            selectedSeason,
            selectedSpeciesId: selectedSpecies?.id,
            withReceptionDate,
            withProcessDate,
            demoShipments: demoPreSettlementsShipmentBoxes,
          }),
          hasFrutic && !hasDemo
            ? getMovementsFromFrutic(
                SPECIES_ID_TO_FRUTIC_SPECIES[selectedSpecies?.id],
                longToShortSeason(selectedSeason),
                session
              )
            : supabase
                .from('movements')
                .select('amount_usd, producer_name')
                .eq('exporter_id', userProfile?.exporter_id)
                .eq('season', selectedSeason)
                .eq('species_id', selectedSpecies?.id),
          getSettlementsByExporterId(
            userProfile?.exporter_id,
            'approved',
            selectedSeason,
            selectedSpecies?.id,
            'all',
            ['*'],
            currentDivisionId()
          ),
        ]);
      if (!isCurrentRequest()) return;
      console.log('unmappedShipmentBoxes', unmappedShipmentBoxes);

      let validSettlements =
        settlementsResponse
          ?.filter(
            (s) =>
              s.total_freight_usd !== null &&
              s.total_freight_usd > 0 &&
              s.exchange_rate !== null &&
              s.settlement_details.length > 0
          )
          .map((s) => ({
            ...s,
            week_etd: `${getWeekKey(s.ship_trip?.etd)}`,
            week_eta: `${getWeekKey(s.ship_trip?.eta)}`,
            settlement_details: s.settlement_details.map((sd) => ({
              ...sd,
              complete_format: `${sd.format}-${sd.unit_weight}KG`,
            })),
          })) || [];

      if (hasDemo) {
        validSettlements = demoPreSettlementsValidSettlements;
      }

      setSettlements(validSettlements);

      let unmappedMovements: Movement[] = [];

      if (hasFrutic && !hasDemo) {
        unmappedMovements =
          movementsResponse?.movements
            .filter(
              (m) =>
                m.concept == 'ANTICIPO' &&
                (selectedSpecies?.id == 4
                  ? m.description.includes('DAGEN')
                  : selectedSpecies?.id == 6
                    ? !m.description.includes('DAGEN')
                    : true)
            )
            .map((movement) => ({
              ...movement,
              amount_usd: movement.equivalentAmount,
              producer_name: movement.producer?.name,
              producer_group: movement.producer?.group?.name || 'NA',
              producer_national_id: movement.producer?.nationalId,
            })) || [];
      } else {
        unmappedMovements = movementsResponse?.data || [];
      }

      if (hasDemo) {
        unmappedMovements = demoPreSettlementsMovements;
      }
      // console.log("TOSETUP unmappedShipmentBoxes", unmappedShipmentBoxes);

      setUnmappedShipmentBoxes(unmappedShipmentBoxes);
      setMovements(unmappedMovements);
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error('Error fetching raw data:', error);
      throw error;
    }
  }, [
    selectedSeason,
    userProfile?.exporter_id,
    userProfile?.exporter?.hasFrutic,
    userProfile?.exporter?.frutic_seasons,
    userProfile?.exporter?.hasDemo,
    userProfile?.exporter?.name,
    // session,
    selectedSpecies,
  ]);

  // Settings management functions - moved before useEffects
  const loadSettings = useCallback(async () => {
    const scope = JSON.stringify([
      userProfile?.exporter_id,
      selectedSeason,
      selectedSpecies?.id,
      session?.user.id,
    ]);
    // Auth refreshes on tab focus must not replace unsaved inline edits.
    if (settingsScope.current === scope) return;
    settingsScope.current = scope;
    const request = ++settingsRequest.current;
    setSettingsBaseline(null);
    setSettingsSaveError(false);
    setSettingsLoadError(false);
    setHasSettings(false);
    setSettingsInheritedFrom(null);
    setLoadingSettings(false);
    if (
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies?.id ||
      !session
    ) {
      return;
    }

    setLoadingSettings(true);

    try {
      const settings = await loadPreSettlementSettings(
        userProfile.exporter_id,
        selectedSeason,
        selectedSpecies.id,
        session
      );

      if (request !== settingsRequest.current) return;

      const criteriaIsDeprecated =
        settings?.calculation_criteria === 'etd' ||
        settings?.calculation_criteria === 'eta';
      const loaded = {
        calculation_criteria: criteriaIsDeprecated
          ? null
          : (settings?.calculation_criteria ?? null),
        product_definition: settings
          ? normalizePresettlementProductDefinition(settings.product_definition)
          : ['variety', 'caliber', 'color', 'box_format', 'box_label'],
        cost_type: settings?.cost_type ?? ('sameCost' as const),
        commission_percentage: settings?.commission_percentage ?? 8,
        grower_exceptions: settings?.grower_exceptions ?? [],
        consider_extra_revenue: settings?.consider_extra_revenue ?? true,
        tariff_configuration:
          settings?.tariff_configuration ??
          legacyTariffConfiguration(settings?.cost_per_format ?? {}),
      };
      setSavedTariffs(loaded.tariff_configuration);
      setGrowerExceptions(loaded.grower_exceptions);
      setGroupBy(loaded.calculation_criteria);
      setProductDefinition(loaded.product_definition);
      setCostType(loaded.cost_type);
      setPercentageTake(loaded.commission_percentage);
      setPercentageText(String(loaded.commission_percentage));
      setConsiderExtraRevenue(loaded.consider_extra_revenue);
      setCostsPerKg(settings?.cost_per_format ?? {});
      setHasSettings(!!settings && !criteriaIsDeprecated);
      setSettingsInheritedFrom(settings?.inherited_from_season ?? null);
      setSettingsBaseline({
        scope,
        snapshot: calculationSettingsSnapshot(loaded),
      });
    } catch (error) {
      if (request !== settingsRequest.current) return;
      // Failed reads are not empty settings. Allow an explicit or auth-refresh retry.
      settingsScope.current = null;
      setSettingsLoadError(true);
      console.error('Failed to load calculation settings:', error);
    } finally {
      if (request === settingsRequest.current) setLoadingSettings(false);
    }
  }, [userProfile?.exporter_id, selectedSeason, selectedSpecies?.id, session]);

  useEffect(() => {
    logUserAction('view', 'page', 'pre-settlements');
  }, []);

  useEffect(() => {
    let active = true;
    async function fetchData() {
      setLoading(true);
      setDataLoadError(false);
      try {
        // console.log("WILL FETCH UNMAPPED DATA");
        await fetchUnmappedData(() => active);
      } catch (error) {
        if (!active) return;
        setDataLoadError(true);
        console.error('Error in fetchData:', error);
      } finally {
        if (active) setLoading(false);
      }
    }
    fetchData();
    return () => {
      active = false;
    };
  }, [fetchUnmappedData]);

  // Load settings when the page loads or when key dependencies change
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Reset bypass when season or species changes
  useEffect(() => {
    setBypassQuadrature(false);
  }, [selectedSeason, selectedSpecies?.id]);

  // Quadrature check - validates that shipments match settlements (shipment-led)
  const quadratureCheck = useMemo(() => {
    if (loading || dataLoadError || !fetchedMappedAndUnmappedFormats) {
      return {
        ready: false,
        mismatches: [] as string[],
        orphanSettlements: [] as string[],
        shipmentReferences: 0,
        settlementReferences: 0,
      };
    }

    // Group shipments by reference (shipment-led approach)
    const shipmentsByReference = new Map<string, ShipmentBox[]>();
    rawShipmentBoxes.forEach((box) => {
      if (!box.reference_number) return;
      const group = shipmentsByReference.get(box.reference_number) || [];
      group.push(box);
      shipmentsByReference.set(box.reference_number, group);
    });

    // Group settlements by reference for lookup
    const settlementsByReference = new Map<string, Settlement[]>();
    settlements.forEach((settlement) => {
      if (!settlement.reference_number) return;
      const group =
        settlementsByReference.get(settlement.reference_number) || [];
      group.push(settlement);
      settlementsByReference.set(settlement.reference_number, group);
    });

    const mismatches: string[] = [];
    const orphanSettlements: string[] = [];
    const quadratureContext = {
      season: selectedSeason,
      speciesId: selectedSpecies?.id,
    };
    const areQuantitiesEqual = (a: number, b: number) =>
      Math.abs(a - b) < 0.0001;

    // Find settlements that don't have corresponding shipments (orphan settlements)
    settlementsByReference.forEach((_, reference) => {
      if (!shipmentsByReference.has(reference)) {
        orphanSettlements.push(reference);
      }
    });

    // Iterate over shipments by reference and check against settlements
    shipmentsByReference.forEach((referenceShipments, reference) => {
      const referenceSettlements = settlementsByReference.get(reference) || [];

      // If no settlements for this reference, it's not a mismatch - just not settled yet
      if (referenceSettlements.length === 0) {
        return;
      }

      // Build shipment totals by product key
      const shipmentTotals = new Map<string, number>();
      referenceShipments.forEach((box) => {
        const key = buildShipmentQuadratureProductKey(box, quadratureContext);
        const current = shipmentTotals.get(key) || 0;
        shipmentTotals.set(key, current + (box.quantity || 0));
      });

      // Build settlement totals by product key
      const settlementTotals = new Map<string, number>();
      referenceSettlements.forEach((settlement) => {
        settlement.settlement_details.forEach((detail) => {
          const key = buildSettlementQuadratureProductKey(
            detail,
            quadratureContext
          );
          const current = settlementTotals.get(key) || 0;
          settlementTotals.set(key, current + (detail.quantity || 0));
        });
      });

      const keys = new Set([
        ...Array.from(shipmentTotals.keys()),
        ...Array.from(settlementTotals.keys()),
      ]);

      let isMatched = true;
      keys.forEach((key) => {
        const shipmentValue = shipmentTotals.get(key) || 0;
        const settlementValue = settlementTotals.get(key) || 0;
        if (
          !hasValidProductKeyFormat(key) ||
          !areQuantitiesEqual(shipmentValue, settlementValue)
        ) {
          isMatched = false;
        }
      });

      if (!isMatched) {
        mismatches.push(reference);
      }
    });

    return {
      ready: true,
      mismatches,
      orphanSettlements,
      shipmentReferences: shipmentsByReference.size,
      settlementReferences: settlementsByReference.size,
    };
  }, [
    loading,
    dataLoadError,
    fetchedMappedAndUnmappedFormats,
    settlements,
    rawShipmentBoxes,
    selectedSeason,
    selectedSpecies?.id,
  ]);

  const hasQuadratureIssues =
    quadratureCheck.ready &&
    (quadratureCheck.mismatches.length > 0 ||
      quadratureCheck.orphanSettlements.length > 0);
  const quadratureBlockedReferences = useMemo(
    () =>
      Array.from(
        new Set([
          ...quadratureCheck.mismatches,
          ...quadratureCheck.orphanSettlements,
        ])
      ),
    [quadratureCheck.mismatches, quadratureCheck.orphanSettlements]
  );
  const shouldBlockPreSettlements =
    !quadratureCheck.ready || (hasQuadratureIssues && !bypassQuadrature);

  // Create effective settlements that exclude references with cuadratura issues
  // when bypass is active.
  const effectiveSettlements = useMemo(() => {
    if (!bypassQuadrature || !hasQuadratureIssues) {
      return settlements;
    }
    const blockedReferences = new Set(quadratureBlockedReferences);
    return settlements.filter(
      (s) => !blockedReferences.has(s.reference_number)
    );
  }, [
    settlements,
    bypassQuadrature,
    hasQuadratureIssues,
    quadratureBlockedReferences,
  ]);

  useEffect(() => {
    async function calculateFOBValues() {
      if (!groupBy || !settingsReady) {
        setShipmentBoxes([]);
        return;
      }
      // console.log("WILL CALCULATE FOB VALUES");
      // console.log("rawShipmentBoxes", rawShipmentBoxes);
      const enrichedShipmentBoxes = await calculateFOBValuesPreSettlementsPool(
        rawShipmentBoxes,
        effectiveSettlements,
        groupBy,
        productDefinition,
        considerExtraRevenue
      );
      // console.log('enrichedShipmentBoxes', enrichedShipmentBoxes);
      setShipmentBoxes(enrichedShipmentBoxes);
    }
    calculateFOBValues();
  }, [
    productDefinition,
    effectiveSettlements,
    rawShipmentBoxes,
    groupBy,
    considerExtraRevenue,
    settingsReady,
  ]);

  // Filter the shipmentBoxes based on selected filters
  const filteredShipmentBoxes = useMemo(() => {
    return shipmentBoxes.filter((box) => {
      const matchesProducer =
        !selectedProducer || box.producer_name === selectedProducer;
      const matchesVariety =
        !selectedVariety || box.variety === selectedVariety;
      const matchesProducerGroup =
        !selectedProducerGroup || box.producer_group === selectedProducerGroup;
      return matchesProducer && matchesVariety && matchesProducerGroup;
    });
  }, [shipmentBoxes, selectedProducer, selectedVariety, selectedProducerGroup]);

  // Process filtered data for table
  const processedData = useMemo(() => {
    const groupedData: ProcessedData = {};

    // Create a set of reference numbers that have actual settlements
    const settledReferences = new Set(
      effectiveSettlements.map((settlement) => settlement.reference_number)
    );

    filteredShipmentBoxes.forEach((box) => {
      if (!groupedData[box.variety]) {
        groupedData[box.variety] = {};
      }

      const caliber = isCollapsed ? 'average' : box.caliber;

      if (!groupedData[box.variety][caliber]) {
        groupedData[box.variety][caliber] = {
          value: 0,
          kilos: 0,
          settledKilos: 0,
          pricedSettledKilos: 0,
          realSettledKilos: 0,
          realValue: 0,
        };
      }

      const fobValue = box.pool_fob_value_per_kg ?? null;
      const weight = Number(box.box_weight || 0) * Number(box.quantity || 0);
      const liquidatedPercentage =
        typeof box.liquidated_percentage === 'number'
          ? box.liquidated_percentage
          : 0;
      const liquidatedKilos = weight * liquidatedPercentage;

      const { rate: costPerKg, commissionPercentage } = growerChargesFor(
        box,
        appliedTariffFor(box).rate
      );

      // Calculate net value: FOB - percentage take - cost per kg
      const netValue =
        fobValue != null
          ? fobValue * (1 - commissionPercentage / 100) - costPerKg
          : null;

      // Always add to total kilos
      groupedData[box.variety][caliber].kilos += weight;

      // Check if this box's reference has actually arrived to settlements
      const isReallySettled = settledReferences.has(box.reference_number);

      if (liquidatedKilos > 0) {
        groupedData[box.variety][caliber].settledKilos += liquidatedKilos;
      }

      if (netValue !== null && liquidatedPercentage > 0 && weight > 0) {
        groupedData[box.variety][caliber].value += netValue * weight;
        groupedData[box.variety][caliber].pricedSettledKilos += weight;
      }

      // Real settlement (only if reference actually exists and has reference FOB)
      if (
        isReallySettled &&
        box.fob_value_per_kg_source === 'reference' &&
        box.reference_fob_value_per_kg != null
      ) {
        const referenceNetValue =
          box.reference_fob_value_per_kg * (1 - commissionPercentage / 100) -
          costPerKg;
        groupedData[box.variety][caliber].realValue +=
          referenceNetValue * weight;
        groupedData[box.variety][caliber].realSettledKilos += weight;
      }
    });

    // Calculate final weighted averages
    Object.keys(groupedData).forEach((variety) => {
      Object.keys(groupedData[variety]).forEach((caliber) => {
        const data = groupedData[variety][caliber];
        if (data.pricedSettledKilos > 0) {
          data.value = data.value / data.pricedSettledKilos;
        }
        if (data.realSettledKilos > 0) {
          data.realValue = data.realValue / data.realSettledKilos;
        }
      });
    });

    return groupedData;
  }, [
    shipmentBoxes,
    filteredShipmentBoxes,
    effectiveSettlements,
    isCollapsed,
    percentageTake,
    appliedTariffFor,
    growerChargesFor,
  ]);

  // Process producer data
  const processedProducerData = useMemo(() => {
    const groupedData: ProcessedProducerData = {};

    // Create a set of reference numbers that have actual settlements
    const settledReferences = new Set(
      effectiveSettlements.map((settlement) => settlement.reference_number)
    );

    filteredShipmentBoxes.forEach((box) => {
      if (!groupedData[box.producer_name]) {
        groupedData[box.producer_name] = {};
      }

      if (!groupedData[box.producer_name][box.variety]) {
        groupedData[box.producer_name][box.variety] = {};
      }

      const caliber = isCollapsed ? 'average' : box.caliber;

      if (!groupedData[box.producer_name][box.variety][caliber]) {
        groupedData[box.producer_name][box.variety][caliber] = {
          value: 0,
          kilos: 0,
          settledKilos: 0,
          pricedSettledKilos: 0,
          realSettledKilos: 0,
          realValue: 0,
        };
      }

      const fobValue = box.pool_fob_value_per_kg ?? null;
      const weight = Number(box.unit_weight || 0) * Number(box.quantity || 0);
      const liquidatedPercentage =
        typeof box.liquidated_percentage === 'number'
          ? box.liquidated_percentage
          : 0;
      const liquidatedKilos = weight * liquidatedPercentage;

      // console.log('fobValue', fobValue);
      // console.log('weight', weight);

      const { rate: costPerKg, commissionPercentage } = growerChargesFor(
        box,
        appliedTariffFor(box).rate
      );

      // console.log('costPerKg', costPerKg);

      // Calculate net value: FOB - percentage take - cost per kg
      const netValue =
        fobValue != null
          ? fobValue * (1 - commissionPercentage / 100) - costPerKg
          : null;

      // console.log('netValue', netValue);

      // Always add to total kilos
      groupedData[box.producer_name][box.variety][caliber].kilos += weight;

      // Check if this box's reference has actually arrived to settlements
      const isReallySettled = settledReferences.has(box.reference_number);

      if (liquidatedKilos > 0) {
        groupedData[box.producer_name][box.variety][caliber].settledKilos +=
          liquidatedKilos;
      }

      if (netValue !== null && liquidatedPercentage > 0 && weight > 0) {
        groupedData[box.producer_name][box.variety][caliber].value +=
          netValue * weight;
        groupedData[box.producer_name][box.variety][
          caliber
        ].pricedSettledKilos += weight;
      }

      // Real settlement (only if reference actually exists and has reference FOB)
      if (
        isReallySettled &&
        box.fob_value_per_kg_source === 'reference' &&
        box.reference_fob_value_per_kg != null
      ) {
        const referenceNetValue =
          box.reference_fob_value_per_kg * (1 - commissionPercentage / 100) -
          costPerKg;
        groupedData[box.producer_name][box.variety][caliber].realValue +=
          referenceNetValue * weight;
        groupedData[box.producer_name][box.variety][caliber].realSettledKilos +=
          weight;
      }
    });

    // Calculate final weighted averages
    Object.keys(groupedData).forEach((producer) => {
      Object.keys(groupedData[producer]).forEach((variety) => {
        Object.keys(groupedData[producer][variety]).forEach((caliber) => {
          const data = groupedData[producer][variety][caliber];
          if (data.pricedSettledKilos > 0) {
            data.value = data.value / data.pricedSettledKilos;
          }
          if (data.realSettledKilos > 0) {
            data.realValue = data.realValue / data.realSettledKilos;
          }
        });
      });
    });

    // console.log('groupedDataFinal', groupedData);

    return groupedData;
  }, [
    filteredShipmentBoxes,
    effectiveSettlements,
    isCollapsed,
    percentageTake,
    appliedTariffFor,
    growerChargesFor,
  ]);

  const fobDebug = useMemo(() => {
    const settledReferences = new Set(
      effectiveSettlements.map((settlement) => settlement.reference_number)
    );

    let settlementKilos = 0;
    let settlementFob = 0;
    let settlementKilosFromQty = 0;
    let settlementKilosDelta = 0;
    let settlementKilosDeltaCount = 0;
    effectiveSettlements.forEach((settlement) => {
      settlement.settlement_details.forEach((detail) => {
        const detailKilos = detail.kilos || 0;
        const detailUnitWeight = Number(detail.unit_weight || 0);
        const detailQuantity = Number(detail.quantity || 0);
        const detailKilosCalc = detailUnitWeight * detailQuantity;
        settlementKilos += detailKilos;
        settlementFob += detail.usd_total_fob || 0;
        settlementKilosFromQty += detailKilosCalc;
        if (detailKilos && detailKilosCalc && detailKilos !== detailKilosCalc) {
          settlementKilosDelta += Math.abs(detailKilos - detailKilosCalc);
          settlementKilosDeltaCount += 1;
        }
      });
    });

    let shipmentKilos = 0;
    let shipmentFob = 0;
    let pricedBoxes = 0;
    let unpricedBoxes = 0;
    let boxWeightDelta = 0;
    let boxWeightDeltaCount = 0;

    shipmentBoxes.forEach((box) => {
      if (!settledReferences.has(box.reference_number)) {
        return;
      }
      const boxWeight = Number(box.box_weight || 0);
      const unitWeight = Number(box.unit_weight || 0);
      const kilos = boxWeight * box.quantity;
      if (box.fob_value_per_kg) {
        pricedBoxes += 1;
        shipmentKilos += kilos;
        shipmentFob += box.fob_value_per_kg * kilos;
      } else {
        unpricedBoxes += 1;
      }
      if (boxWeight && unitWeight && boxWeight !== unitWeight) {
        boxWeightDelta += Math.abs(boxWeight - unitWeight);
        boxWeightDeltaCount += 1;
      }
    });

    return {
      settlementKilos,
      settlementFob,
      settlementKilosFromQty,
      settlementKilosDelta,
      settlementKilosDeltaCount,
      shipmentKilos,
      shipmentFob,
      pricedBoxes,
      unpricedBoxes,
      boxWeightDelta,
      boxWeightDeltaCount,
    };
  }, [shipmentBoxes, effectiveSettlements]);

  const fobKeyDeltas = useMemo(() => {
    if (groupBy !== 'ship') {
      return [];
    }

    const fields = productDefinition;
    const settlementAgg = new Map<
      string,
      { kilos: number; fob: number; shipTripId: string; productKey: string }
    >();
    const shipmentAgg = new Map<
      string,
      { kilos: number; shipTripId: string; productKey: string }
    >();
    const referenceToShipTripId = new Map<string, string>();

    effectiveSettlements.forEach((settlement) => {
      if (settlement.reference_number && settlement.ship_trip_id) {
        referenceToShipTripId.set(
          settlement.reference_number,
          settlement.ship_trip_id
        );
      }

      const shipTripId = settlement.ship_trip_id;
      if (!shipTripId) return;

      settlement.settlement_details.forEach((detail) => {
        const productKey = buildPoolProductKey(detail, fields);
        const key = `${shipTripId}::${productKey}`;
        const current = settlementAgg.get(key) || {
          kilos: 0,
          fob: 0,
          shipTripId,
          productKey,
        };
        current.kilos += Number(detail.kilos || 0);
        current.fob += Number(detail.usd_total_fob || 0);
        settlementAgg.set(key, current);
      });
    });

    shipmentBoxes.forEach((box) => {
      const shipTripId = referenceToShipTripId.get(box.reference_number);
      if (!shipTripId) return;
      const productKey = buildPoolProductKey(box, fields);
      const key = `${shipTripId}::${productKey}`;
      const current = shipmentAgg.get(key) || {
        kilos: 0,
        shipTripId,
        productKey,
      };
      current.kilos += Number(box.box_weight || 0) * Number(box.quantity || 0);
      shipmentAgg.set(key, current);
    });

    const keys = new Set([
      ...Array.from(settlementAgg.keys()),
      ...Array.from(shipmentAgg.keys()),
    ]);

    const deltas = Array.from(keys).map((key) => {
      const settlement = settlementAgg.get(key);
      const shipment = shipmentAgg.get(key);
      const settlementKilos = settlement?.kilos || 0;
      const shipmentKilos = shipment?.kilos || 0;
      const avgFobPerKg =
        settlement && settlement.kilos > 0
          ? settlement.fob / settlement.kilos
          : 0;
      const deltaKilos = shipmentKilos - settlementKilos;
      const impact = deltaKilos * avgFobPerKg;
      return {
        shipTripId: settlement?.shipTripId || shipment?.shipTripId || 'NA',
        productKey: settlement?.productKey || shipment?.productKey || 'NA',
        settlementKilos,
        shipmentKilos,
        deltaKilos,
        avgFobPerKg,
        impact,
      };
    });

    return deltas
      .sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact))
      .slice(0, 12);
  }, [groupBy, productDefinition, effectiveSettlements, shipmentBoxes]);

  // console.log('processedProducerData', processedProducerData);

  // Process movements data
  const processedMovements = useMemo(() => {
    const grouped: { [producer: string]: { amount: number; kilos: number } } =
      {};

    movements.forEach((movement) => {
      if (!grouped[movement.producer_name]) {
        grouped[movement.producer_name] = { amount: 0, kilos: 0 };
      }
      grouped[movement.producer_name].amount += movement.amount_usd;
    });

    return grouped;
  }, [movements]);

  // Get unique producers and varieties for filters
  const producers = [
    ...new Set(shipmentBoxes.map((box) => box.producer_name)),
  ].sort();
  const varieties = [...new Set(shipmentBoxes.map((box) => box.variety))];
  const producerGroups = [
    ...new Set(shipmentBoxes.map((box) => box.producer_group)),
  ];

  const producerGroupOptions = useMemo(
    () =>
      producerGroups.map((group) => ({
        key: String(group),
        label: String(group),
        value: String(group),
      })),
    [producerGroups]
  );

  const producerOptions = useMemo(
    () =>
      producers.map((producer) => ({
        key: String(producer),
        label: String(producer),
        value: String(producer),
      })),
    [producers]
  );

  const varietyOptions = useMemo(
    () =>
      varieties.map((variety) => ({
        key: String(variety),
        label: String(variety),
        value: String(variety),
      })),
    [varieties]
  );

  // Settings management functions
  const handleSaveSettings = async () => {
    if (
      !userProfile?.exporter_id ||
      !selectedSeason ||
      !selectedSpecies?.id ||
      !session ||
      !groupBy ||
      loading ||
      loadingSettings ||
      savingSettings ||
      !tariffsValid ||
      !growerExceptionsValid ||
      !settingsReady
    ) {
      console.error('Missing required data for saving settings');
      return;
    }

    setSavingSettings(true);
    setSettingsSaveError(false);
    const savingScope = settingsContext;
    const savingSnapshot = currentSettingsSnapshot;

    const settings: PreSettlementSettings = {
      calculation_criteria: groupBy!,
      product_definition: productDefinition,
      cost_type: costType,
      commission_percentage: percentageTake,
      cost_per_format: costsPerKg,
      tariff_configuration: tariffConfig,
      grower_exceptions: growerExceptions,
      consider_extra_revenue: considerExtraRevenue,
    };

    const result = await savePreSettlementSettings(
      userProfile.exporter_id,
      selectedSeason,
      selectedSpecies.id,
      settings,
      session
    );

    if (result && settingsScope.current === savingScope) {
      setHasSettings(true);
      setSettingsInheritedFrom(null);
      setSettingsBaseline({ scope: savingScope, snapshot: savingSnapshot });
      console.log('Settings saved successfully');
      onSaveModalOpenChange();
    } else if (!result && settingsScope.current === savingScope) {
      setSettingsSaveError(true);
      console.error('Failed to save settings');
    }

    setSavingSettings(false);
  };

  const exportShipmentData = () => {
    if (!groupBy || !settingsReady || !tariffsValid || !growerExceptionsValid)
      return;
    setLoadingExport(true);

    // Use setTimeout with 0 delay to move the processing to the next event loop cycle
    // This allows the UI to update and show the loading indicator immediately
    setTimeout(() => {
      try {
        // Build lookup maps from settlements
        const settledReferences = new Set(
          settlements.map((s) => s.reference_number)
        );
        const sinisterByRef = new Map(
          settlements.map((s) => [s.reference_number, s.is_sinister])
        );
        const fobTotalByRef = new Map<string, number>();
        settlements.forEach((s) => {
          const fob = s.settlement_details.reduce(
            (sum, d) => sum + (d.usd_total_fob || 0),
            0
          );
          fobTotalByRef.set(
            s.reference_number,
            (fobTotalByRef.get(s.reference_number) || 0) + fob
          );
        });

        const hasProcessInternalId = filteredShipmentBoxes.some(
          (box) => box.process_internal_id
        );

        // Transform shipment data to rows
        const rows = filteredShipmentBoxes.map((box) => {
          const productCompleteKey = buildShipmentQuadratureProductKey(box, {
            season: selectedSeason,
            speciesId: selectedSpecies?.id,
          });
          const productLiquidationKey = buildPoolProductKey(
            box,
            productDefinition
          );
          const poolWeek = getPoolGroupKey(box, groupBy);
          const poolCriteriaLabel =
            groupBy === 'reception'
              ? 'SEMANA_RECEPCION'
              : groupBy === 'process'
                ? 'SEMANA_PROCESO'
                : groupBy === 'packing'
                  ? 'SEMANA_PACKING'
                  : groupBy === 'ship'
                    ? 'NAVE'
                    : 'REFERENCIA';
          const poolFobPerKg = box.pool_fob_value_per_kg ?? null;
          const appliedTariff = appliedTariffFor(box);
          const {
            rate: costPerKg,
            commissionPercentage,
            exception,
          } = growerChargesFor(box, appliedTariff.rate);
          const costDimensions = tariffDimensions(box, tariffConfig);

          const netValue =
            poolFobPerKg != null
              ? poolFobPerKg * (1 - commissionPercentage / 100) - costPerKg
              : null;

          const totalAnticipo = movements
            .filter((m) => m.producer_name === box.producer_name)
            .reduce((sum, movement) => sum + movement.amount_usd, 0);

          const totalKilos = filteredShipmentBoxes
            .filter((b) => b.producer_name === box.producer_name)
            .reduce((sum, b) => sum + b.box_weight * b.quantity, 0);

          const kilosBox = box.box_weight * box.quantity;
          const anticipoShare = (totalAnticipo * kilosBox) / totalKilos;
          const anticipoKg = anticipoShare / kilosBox;

          const estadoLiquidacion = settledReferences.has(box.reference_number)
            ? 'Recibida'
            : 'Pendiente';
          const siniestro = sinisterByRef.has(box.reference_number)
            ? sinisterByRef.get(box.reference_number)
              ? 'Si'
              : 'No'
            : '';
          const fobTotal =
            poolFobPerKg != null
              ? Number((poolFobPerKg * kilosBox).toFixed(2))
              : null;
          const fobTotalPool =
            poolFobPerKg != null
              ? Number((poolFobPerKg * kilosBox).toFixed(2))
              : null;

          const referenceFobPerKg = box.reference_fob_value_per_kg ?? null;
          const fobTotalReference =
            referenceFobPerKg != null
              ? Number((referenceFobPerKg * kilosBox).toFixed(2))
              : null;

          return {
            Referencia: box.reference_number,
            Factura: box.invoice_number || '',
            'Orden de Venta': box.sale_order || '',
            'Estado Liquidación': estadoLiquidacion,
            Cliente: box.receiver,
            'Cláusula Venta': box.original_incoterm || '',
            'Tipo Flete': box.freight_type || '',
            'Tipo de Negocio': box.business_type || '',
            Siniestro: siniestro,
            'Tipo de Nave': box.ship_type || '',
            Nave: box.ship_name || '',
            Booking: box.booking_number || box.booking_awb || '',
            AWB: box.awb_number || '',
            Contenedor: box.container_number,
            'Región de Venta': box.sale_region || '',
            'País Destino': box.ship_destination_country || '',
            'Puerto Destino': box.ship_destination_port || '',
            'Mercado de Venta': box.sale_market || '',
            Packing: box.packing_name,
            Grupo: box.producer_group,
            Productor: box.producer_name,
            Categoría: box.category,
            'Tipo de Frío': box.cold_type || '',
            'Segregación Fruta': box.fruit_segregation || '',
            Pallet: box.pallet,
            Lote: box.lot || '',
            Variedad: box.variety,
            'Variedad Etiquetada': box.labeled_variety,
            Calibre: box.caliber,
            Color: box.color,
            'Código Embalaje': box.box_type,
            Etiqueta: box.box_label,
            Formato: box.box_format,
            'Peso Caja': box.box_weight,
            Cantidad: box.quantity,
            Kilos: Number(kilosBox.toFixed(2)),
            'Fecha Recepción': box.reception_date || '',
            'Fecha Proceso': box.process_date || '',
            ...(hasProcessInternalId
              ? { 'Nro Proceso': box.process_internal_id || '' }
              : {}),
            'Fecha Despacho Packing': box.packing_etd || '',
            'Fecha ETD': box.ship_etd,
            'Fecha ETA': box.ship_eta,
            'Semana Recepción': box.reception_date
              ? getWeekKey(box.reception_date)
              : '',
            'Semana Proceso': box.process_date
              ? getWeekKey(box.process_date)
              : '',
            'Semana Despacho Packing': box.packing_etd
              ? getWeekKey(box.packing_etd)
              : '',
            'Semana ETD': box.week_etd || '',
            'Semana ETA': box.week_eta || '',
            // 'FOB Total': fobTotal,
            'Valor FOB Kilo-Producto-Referencia': referenceFobPerKg
              ? Number(referenceFobPerKg.toFixed(2))
              : null,
            'FOB Total Referencia': fobTotalReference
              ? Number(fobTotalReference.toFixed(2))
              : null,
            'Valor FOB Kilo-Producto-Pool': box.pool_fob_value_per_kg
              ? Number(box.pool_fob_value_per_kg)
              : null,
            'FOB Total Pool': fobTotalPool,
            '% Liquidado Pool':
              typeof box.liquidated_percentage === 'number'
                ? Number((box.liquidated_percentage * 100).toFixed(2))
                : null,
            // 'FOB USD/kg': poolFobPerKg ? Number(poolFobPerKg) : null,
            'Comisión Exportadora %': commissionPercentage,
            'Comisión Exportadora/kg':
              poolFobPerKg != null
                ? (commissionPercentage * poolFobPerKg) / 100
                : null,
            'Excepción Comisión':
              exception?.commissionPercentage != null ? 'Sí' : 'No',
            'Tarifa Base USD/kg': appliedTariff.rate,
            'Excepción Tarifa': exception?.tariff
              ? {
                  replace: 'Tarifa de reemplazo',
                  'percentage-discount': 'Descuento porcentual',
                  'amount-discount': 'Descuento fijo por kg',
                }[exception.tariff.mode]
              : '',
            'Valor Excepción Tarifa': exception?.tariff?.value ?? null,
            'Tarifa USD/kg': costPerKg,
            'Criterio Tarifa': tariffSummary,
            'Semana Tarifa': costDimensions.week ?? '',
            'Aplicación Tarifa': tariffApplicationLabel,
            'Origen Tarifa':
              appliedTariff.source === 'weighted-average'
                ? 'Promedio ponderado por kg'
                : appliedTariff.source === 'specific'
                  ? 'Específica'
                  : 'General',
            'Retorno Productor Neto USD/kg': netValue ? Number(netValue) : null,
            'Anticipo/kg': anticipoKg ? Number(anticipoKg) : null,
            'Retorno Productor Neto USD':
              netValue != null && kilosBox != null
                ? Number(netValue * kilosBox)
                : null,
            'Producto-Completo': productCompleteKey,
            'Producto-Liquidacion': productLiquidationKey,
            'Criterio Pool': poolCriteriaLabel,
            'Semana Pool': poolWeek,
          };
        });

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rows);

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Detalle');

        // Save workbook
        XLSX.writeFile(wb, `preliquidacion_${selectedSeason}.xlsx`);
      } catch (error) {
        console.error('Error exporting data:', error);
      } finally {
        setLoadingExport(false);
      }
    }, 0);
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <PageLayout className={hasCalculationChanges ? 'pb-40' : ''}>
        {dataLoadError && (
          <Alert variant='danger' showIcon className='mb-6'>
            {t(
              'No se pudieron cargar los datos. Recarga la página para volver a intentar.'
            )}
          </Alert>
        )}
        {!quadratureCheck.ready && !dataLoadError && (
          <Card className='mb-6'>
            <CardBody className='p-6'>
              <div className='flex items-center gap-3'>
                <Spinner size='sm' />
                <p className='text-sm text-default-500'>
                  {t(
                    'Validando cuadratura de las bases de despachos y liquidaciones...'
                  )}
                </p>
              </div>
            </CardBody>
          </Card>
        )}
        {quadratureCheck.ready && !hasQuadratureIssues && (
          <Alert variant='success' className='mb-6'>
            <div className='flex flex-col gap-1 md:flex-row md:items-center md:justify-between'>
              <div>
                <p className='text-sm font-semibold text-green-700 dark:text-green-300'>
                  {t('Cuadratura OK')}
                </p>
                <p className='text-xs text-green-600 dark:text-green-400'>
                  {t(
                    'Las liquidaciones aprobadas cuadran con la base de despachos para las referencias liquidadas.'
                  )}
                </p>
              </div>
              <div className='flex flex-wrap gap-2 text-xs text-green-700 dark:text-green-300'>
                <Chip size='sm' color='success' variant='flat'>
                  {quadratureCheck.shipmentReferences}{' '}
                  {t('referencias despachadas')}
                </Chip>
                <Chip size='sm' color='success' variant='flat'>
                  {quadratureCheck.settlementReferences}{' '}
                  {t('referencias liquidadas')}
                </Chip>
              </div>
            </div>
          </Alert>
        )}
        {hasQuadratureIssues && (
          <Card shadow='sm' className='mb-6 border-none'>
            <CardBody className='p-0'>
              {/* Header */}
              <div className='bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-lg'>
                <div className='flex items-center gap-3'>
                  <div className='flex-shrink-0 w-10 h-10 bg-background/20 rounded-full flex items-center justify-center'>
                    <svg
                      className='w-5 h-5 text-white'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className='text-lg font-semibold text-white'>
                      {t('Cuadratura Pendiente')}
                    </h3>
                    <p className='text-sm text-red-100'>
                      {t('Existen diferencias entre embarques y liquidaciones')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className='p-6 space-y-6'>
                <p className='text-sm text-default-500'>
                  {t(
                    'Para garantizar que los cálculos sean correctos, ambas bases deben cuadrar. Revisa y corrige las referencias listadas antes de usar esta página.'
                  )}
                </p>

                {/* Mismatches Section */}
                {quadratureCheck.mismatches.length > 0 && (
                  <Alert variant='danger' showIcon>
                    <div className='flex items-center gap-2 mb-3'>
                      <span className='font-semibold'>
                        {t('Referencias con cuadratura pendiente')}
                      </span>
                      <Chip size='sm' variant='flat'>
                        {quadratureCheck.mismatches.length}
                      </Chip>
                    </div>
                    <p className='text-xs font-normal mb-3'>
                      {t(
                        'Estas referencias tienen diferencias entre los despachos y las liquidaciones'
                      )}
                    </p>
                    <div className='flex flex-wrap gap-2'>
                      {quadratureCheck.mismatches
                        .slice(0, 12)
                        .map((reference) => (
                          <Chip key={reference} size='sm' variant='flat'>
                            {reference}
                          </Chip>
                        ))}
                      {quadratureCheck.mismatches.length > 12 && (
                        <Chip size='sm' variant='flat'>
                          +{quadratureCheck.mismatches.length - 12} {t('más')}
                        </Chip>
                      )}
                    </div>
                  </Alert>
                )}

                {/* Orphan Settlements Section */}
                {quadratureCheck.orphanSettlements.length > 0 && (
                  <Alert variant='warning' showIcon>
                    <div className='flex items-center gap-2 mb-3'>
                      <span className='font-semibold'>
                        {t('Liquidaciones sin despachos asociados')}
                      </span>
                      <Chip size='sm' variant='flat'>
                        {quadratureCheck.orphanSettlements.length}
                      </Chip>
                    </div>
                    <p className='text-xs font-normal mb-3'>
                      {t(
                        'Estas liquidaciones no contribuirán a los resultados de la preliquidación'
                      )}
                    </p>
                    <div className='flex flex-wrap gap-2'>
                      {quadratureCheck.orphanSettlements
                        .slice(0, 12)
                        .map((reference) => (
                          <Chip key={reference} size='sm' variant='flat'>
                            {reference}
                          </Chip>
                        ))}
                      {quadratureCheck.orphanSettlements.length > 12 && (
                        <Chip size='sm' variant='flat'>
                          +{quadratureCheck.orphanSettlements.length - 12}{' '}
                          {t('más')}
                        </Chip>
                      )}
                    </div>
                  </Alert>
                )}

                {/* Bypass Option */}
                <Alert variant='warning' showIcon>
                  <p>{t('¿Deseas continuar de todas formas?')}</p>
                  <p className='text-xs font-normal mt-1'>
                    {t(
                      'Los resultados pueden no ser exactos. Los despachos no cuadrados se asumirán como no liquidados.'
                    )}
                  </p>
                  <Button
                    variant='flat'
                    size='sm'
                    className='mt-3'
                    onClick={() => setBypassQuadrature(true)}
                  >
                    {t('Continuar bajo mi responsabilidad')}
                  </Button>
                </Alert>
              </div>
            </CardBody>
          </Card>
        )}
        {hasQuadratureIssues && bypassQuadrature && (
          <Alert variant='warning' showIcon className='mb-6'>
            <p>{t('Resultados aproximados')}</p>
            <p className='text-xs font-normal mt-0.5'>
              {t('Referencias excluidas:')}{' '}
              {quadratureBlockedReferences.slice(0, 5).join(', ')}
              {quadratureBlockedReferences.length > 5 &&
                ` ${t('y')} ${quadratureBlockedReferences.length - 5} ${t('más')}`}
            </p>
          </Alert>
        )}
        {groupBy === 'ship' && !shouldBlockPreSettlements && (
          <Card className='mb-6'>
            <CardBody className='p-6'>
              <p className='text-sm font-semibold text-default-600 mb-2'>
                {t('Resumen FOB Nave')}
              </p>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-default-500'>
                <div>
                  {t('Kilos Liquidaciones')}:{' '}
                  {Number(fobDebug.settlementKilos).toFixed(2)}
                </div>
                <div>
                  {t('Kilos Liquidaciones (unit_weight * quantity)')}:{' '}
                  {Number(fobDebug.settlementKilosFromQty).toFixed(2)}
                </div>
                <div>
                  {t('FOB Liquidaciones (USD)')}:{' '}
                  {Number(fobDebug.settlementFob).toFixed(2)}
                </div>
                <div>
                  {t('Delta Kilos Liquidaciones')}:{' '}
                  {Number(fobDebug.settlementKilosDelta).toFixed(2)} (
                  {fobDebug.settlementKilosDeltaCount})
                </div>
                <div>
                  {t('Kilos Preliquidacion (cajas con FOB)')}:{' '}
                  {Number(fobDebug.shipmentKilos).toFixed(2)}
                </div>
                <div>
                  {t('FOB Preliquidacion (USD)')}:{' '}
                  {Number(fobDebug.shipmentFob).toFixed(2)}
                </div>
                <div>
                  {t('Cajas con FOB')}: {fobDebug.pricedBoxes}
                </div>
                <div>
                  {t('Cajas sin FOB')}: {fobDebug.unpricedBoxes}
                </div>
                <div>
                  {t('Delta Peso Caja vs Unidad')}:{' '}
                  {Number(fobDebug.boxWeightDelta).toFixed(2)} (
                  {fobDebug.boxWeightDeltaCount})
                </div>
              </div>
            </CardBody>
          </Card>
        )}
        {groupBy === 'ship' && !shouldBlockPreSettlements && (
          <Card className='mb-6'>
            <CardBody className='p-6'>
              <p className='text-sm font-semibold text-default-600 mb-2'>
                {t('Top deltas FOB por Nave y Producto')}
              </p>
              {fobKeyDeltas.length === 0 ? (
                <p className='text-sm text-default-500'>
                  {t('Sin diferencias para mostrar.')}
                </p>
              ) : (
                <div className='grid grid-cols-1 gap-2 text-xs text-default-500'>
                  {fobKeyDeltas.map((row, index) => (
                    <div
                      key={`${row.shipTripId}-${row.productKey}-${index}`}
                      className='rounded border border-divider bg-default-100 p-2'
                    >
                      <div>
                        {t('Nave')}: {row.shipTripId}
                      </div>
                      <div className='truncate'>
                        {t('Producto')}: {row.productKey}
                      </div>
                      <div>
                        {t('Kilos Liquidaciones')}:{' '}
                        {Number(row.settlementKilos).toFixed(2)}
                      </div>
                      <div>
                        {t('Kilos Preliquidacion')}:{' '}
                        {Number(row.shipmentKilos).toFixed(2)}
                      </div>
                      <div>
                        {t('Delta Kilos')}: {Number(row.deltaKilos).toFixed(2)}
                      </div>
                      <div>
                        {t('FOB Promedio (USD/kg)')}:{' '}
                        {Number(row.avgFobPerKg).toFixed(4)}
                      </div>
                      <div>
                        {t('Impacto FOB (USD)')}:{' '}
                        {Number(row.impact).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}
        {!shouldBlockPreSettlements && (
          <>
            <Header
              title={t('Preliquidación Productor')}
              description={t(
                'Calcula cuánto está dando la temporada seleccionada por variedad y productor.'
              )}
              actions={
                <>
                  {exporterUsesFrutic(userProfile?.exporter, selectedSeason) && (
                    <Chip
                      startContent={
                        <div className='relative'>
                          <div className='absolute w-2 h-2 rounded-full bg-green-50 dark:bg-green-9500 animate-ping opacity-75' />
                          <div className='relative w-2 h-2 rounded-full bg-green-50 dark:bg-green-9500' />
                        </div>
                      }
                      variant='flat'
                      color='success'
                      className='text-green-800 font-medium px-3 py-2 bg-green-50 dark:bg-green-950 border border-green-200 shadow-sm hover:bg-green-100 transition-colors duration-200'
                    >
                      {t('Portal Productores Conectado')}
                    </Chip>
                  )}
                  <Button
                    color='primary'
                    variant='solid'
                    startContent={<Icon icon='tabler:download' />}
                    onClick={exportShipmentData}
                    isLoading={loadingExport}
                    isDisabled={
                      loadingExport ||
                      !settingsReady ||
                      !tariffsValid ||
                      !growerExceptionsValid
                    }
                  >
                    {loadingExport ? t('Exportando...') : t('Exportar Detalle')}
                  </Button>
                </>
              }
            />
            <div className='mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-divider pb-2'>
              <div
                role='tablist'
                aria-label={t('Preliquidación Productor')}
                className='flex gap-1'
              >
                {[true, false].map((configuration) => (
                  <button
                    key={String(configuration)}
                    id={
                      configuration
                        ? 'presettlement-config-tab'
                        : 'presettlement-results-tab'
                    }
                    type='button'
                    role='tab'
                    aria-selected={sectionsVisible === configuration}
                    aria-controls={
                      configuration
                        ? 'presettlement-config'
                        : 'presettlement-results'
                    }
                    tabIndex={sectionsVisible === configuration ? 0 : -1}
                    onClick={() => setSectionsVisible(configuration)}
                    onKeyDown={(event) => {
                      if (
                        ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(
                          event.key
                        )
                      ) {
                        event.preventDefault();
                        const next =
                          event.key === 'Home'
                            ? true
                            : event.key === 'End'
                              ? false
                              : !configuration;
                        setSectionsVisible(next);
                        document
                          .getElementById(
                            next
                              ? 'presettlement-config-tab'
                              : 'presettlement-results-tab'
                          )
                          ?.focus();
                      }
                    }}
                    className={`flex items-center gap-2 border-b-2 px-5 py-3 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${sectionsVisible === configuration ? 'border-primary text-foreground' : 'border-transparent text-default-500 hover:text-foreground'}`}
                  >
                    {configuration ? t('Configuración') : t('Resultados')}
                  </button>
                ))}
              </div>
            </div>
            {userProfile?.exporter?.hasDemo && (
              <Alert className='mb-4'>
                {t(
                  'Estás viendo una versión de prueba con datos ficticios. Contrata CherryMarket para cargar tus datos reales.'
                )}
              </Alert>
            )}

            {(loadingSettings || (!settingsReady && !settingsLoadError)) && (
              <Alert variant='warning' className='mb-4'>
                <div className='flex items-center gap-2'>
                  <Icon icon='tabler:loader-2' className='animate-spin' />
                  {t('Cargando configuración guardada...')}
                </div>
              </Alert>
            )}

            {hasSettings &&
              settingsReady &&
              !hasCalculationChanges &&
              (settingsInheritedFrom ? (
                <Alert showIcon className='mb-4'>
                  {t(
                    'Se aplicó la configuración de la temporada {{season}}. Si la cambias y guardas, queda como propia de {{current}}.',
                    { season: settingsInheritedFrom, current: selectedSeason }
                  )}
                </Alert>
              ) : (
                <Alert variant='success' className='mb-4'>
                  ✓{' '}
                  {t(
                    'Se ha aplicado tu configuración guardada para esta temporada y especie.'
                  )}
                </Alert>
              ))}

            {settingsLoadError && (
              <div role='alert' className='mb-4'>
                <Alert variant='danger' showIcon>
                  <div className='flex flex-wrap items-center justify-between gap-3'>
                    <p>
                      {t(
                        'No se pudo cargar la configuración. Reintenta antes de realizar cambios.'
                      )}
                    </p>
                    <Button
                      variant='flat'
                      onPress={() => void loadSettings()}
                    >
                      {t('Reintentar')}
                    </Button>
                  </div>
                </Alert>
              </div>
            )}
            {settingsReady && (
              <>
                <div
                  id='presettlement-config'
                  role='tabpanel'
                  aria-labelledby='presettlement-config-tab'
                  hidden={!sectionsVisible}
                >
                  <div className='space-y-6'>
                    <Card shadow='sm'>
                      <CardBody className='p-6'>
                        <div className='grid grid-cols-1 items-start gap-6 lg:grid-cols-3'>
                          <Select
                            label={t('Pool de Liquidación')}
                            selectedKeys={groupBy ? [groupBy] : []}
                            placeholder={t('Seleccione un criterio')}
                            onChange={(e) =>
                              setGroupBy(e.target.value as PoolCriteria)
                            }
                            className='w-full'
                            variant='bordered'
                            labelPlacement='outside'
                            classNames={{
                              trigger:
                                'bg-background border border-divider shadow-none min-h-11',
                              label: 'text-default-500 font-medium',
                              description: 'text-default-500',
                            }}
                            isInvalid={!groupBy}
                            errorMessage={
                              !groupBy
                                ? t('Debe seleccionar un criterio de cálculo')
                                : undefined
                            }
                            description={
                              groupBy
                                ? t('Semana que define el pool de liquidación')
                                : undefined
                            }
                          >
                            <SelectItem key='reference' value='reference'>
                              {t('Sin Pool')}
                            </SelectItem>
                            <SelectItem
                              key='reception'
                              value='reception'
                              isDisabled={
                                !withReceptionDate ||
                                poolAvailability.reception.disabled
                              }
                              description={
                                !withReceptionDate
                                  ? t('Datos de recepción no cargados')
                                  : poolAvailability.reception.disabled
                                    ? poolAvailability.reception.reason
                                    : undefined
                              }
                            >
                              {t('Semana Recepción')}
                            </SelectItem>
                            <SelectItem
                              key='process'
                              value='process'
                              isDisabled={
                                !withProcessDate ||
                                poolAvailability.process.disabled
                              }
                              description={
                                !withProcessDate
                                  ? t('Datos de proceso no cargados')
                                  : poolAvailability.process.disabled
                                    ? poolAvailability.process.reason
                                    : undefined
                              }
                            >
                              {t('Semana Proceso')}
                            </SelectItem>
                            <SelectItem
                              key='packing'
                              value='packing'
                              isDisabled={poolAvailability.packing.disabled}
                              description={
                                poolAvailability.packing.disabled
                                  ? poolAvailability.packing.reason
                                  : undefined
                              }
                            >
                              {t('Semana Despacho Packing')}
                            </SelectItem>
                            {/* ETD and ETA pool options hidden — shipment box dates not reliable */}
                          </Select>{' '}
                          <Select
                            label={t('Aplicación de Tarifas')}
                            selectedKeys={new Set([costType])}
                            disallowEmptySelection
                            onSelectionChange={(keys) => {
                              const mode = Array.from(keys)[0];
                              if (mode === 'sameCost' || mode === 'formatCost')
                                setCostType(mode);
                            }}
                            className='w-full'
                            variant='bordered'
                            labelPlacement='outside'
                            classNames={{
                              trigger:
                                'bg-background border border-divider shadow-none min-h-11',
                              label: 'text-default-500 font-medium',
                              description: 'text-default-500',
                            }}
                            description={
                              costType === 'sameCost'
                                ? t(
                                    'Aplica a todas las cajas el promedio ponderado por kg de los despachos de la temporada y especie.'
                                  )
                                : t(
                                    'Aplica a cada caja su tarifa según packing, formato o código de embalaje y semana configurados.'
                                  )
                            }
                          >
                            <SelectItem key='sameCost' value='sameCost'>
                              {t('Misma Tarifa')}
                            </SelectItem>
                            <SelectItem key='formatCost' value='formatCost'>
                              {t('Separar Tarifa')}
                            </SelectItem>
                          </Select>
                          <div>
                            <Input
                              id='presettlement-commission'
                              type='number'
                              label={t('Comisión exportadora')}
                              labelPlacement='outside'
                              className='max-w-[150px]'
                              value={percentageText}
                              onValueChange={(value) => {
                                setPercentageText(value);
                                setPercentageTake(Number(value));
                              }}
                              min='0'
                              max='100'
                              step='0.1'
                              endContent={
                                <span className='pointer-events-none text-sm text-default-500'>
                                  %
                                </span>
                              }
                            />
                            <p className='mt-2 text-xs text-default-500'>
                              {t('Simula el impacto de cambiar el porcentaje')}
                            </p>
                          </div>
                        </div>
                        <div className='mt-6'>
                          <p
                            id='presettlement-variables-label'
                            className='mb-2 text-sm font-medium text-default-500'
                          >
                            {t('Variables para Liquidar')}
                          </p>
                          <div
                            role='group'
                            aria-labelledby='presettlement-variables-label'
                            className='flex flex-wrap gap-2'
                          >
                            {[
                              ['variety', 'Variedad'],
                              ['caliber', 'Calibre'],
                              ['color', 'Color'],
                              ...(selectedSpecies?.id !== 2
                                ? [['category', 'Categoría']]
                                : []),
                              ['box_format', 'Formato'],
                              ['box_label', 'Etiqueta'],
                              ['fruit_segregation', 'Segregación'],
                            ].map(([key, label]) => {
                              const selected = productDefinition.includes(key);
                              const fixed =
                                key === 'variety' || key === 'caliber';
                              return (
                                <button
                                  key={key}
                                  type='button'
                                  aria-pressed={selected}
                                  aria-disabled={fixed}
                                  onClick={() => {
                                    if (!fixed)
                                      setProductDefinition((previous) =>
                                        selected
                                          ? previous.filter(
                                              (item) => item !== key
                                            )
                                          : [...previous, key]
                                      );
                                  }}
                                  className={`rounded-full border px-4 py-1.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${selected ? 'border-primary/50 bg-primary/10 text-primary' : 'border-divider text-default-500 hover:border-default-400 hover:text-foreground'} ${fixed ? 'cursor-default' : ''}`}
                                >
                                  {t(label)}
                                </button>
                              );
                            })}
                          </div>
                          <p className='mt-2 text-xs text-default-500'>
                            {t(
                              'El formato incluye el tipo de envase y el peso bulto, por ejemplo CAJA-5KG o 2IN1-5KG.'
                            )}
                          </p>
                        </div>
                        <div className='mt-3'>
                          {effectiveSettlements.some(
                            (s) =>
                              s.total_other_revenue_local &&
                              s.total_other_revenue_local !== 0
                          ) && (
                            <Switch
                              size='sm'
                              isSelected={considerExtraRevenue}
                              onValueChange={setConsiderExtraRevenue}
                            >
                              <span className='text-sm'>
                                {t('Considerar ingresos adicionales')}
                              </span>
                            </Switch>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                    <TariffEditor
                      key={`${userProfile?.exporter_id}-${selectedSeason}-${selectedSpecies?.id}`}
                      boxes={rawShipmentBoxes}
                      value={tariffConfig}
                      onChange={setSavedTariffs}
                      onValidityChange={setTariffsValid}
                      formatMapping={
                        <BoxFormatMappingSection
                          speciesId={selectedSpecies?.id}
                          season={selectedSeason}
                          compact
                          embedded
                        />
                      }
                      disabled={loading || loadingSettings || savingSettings}
                    />
                    <GrowerExceptionsEditor
                      growers={exceptionGrowers}
                      value={growerExceptions}
                      onChange={setGrowerExceptions}
                      defaultCommission={percentageTake}
                      disabled={loading || loadingSettings || savingSettings}
                    />
                    {tariffConfig.packaging === 'code' &&
                      productDefinition.includes('box_format') && (
                        <BoxFormatMappingSection
                          speciesId={selectedSpecies?.id}
                          season={selectedSeason}
                          compact
                          description={t(
                            'Agrupa la liquidación por formato; las tarifas por código no dependen del mapeo.'
                          )}
                        />
                      )}
                  </div>
                </div>
                <div
                  id='presettlement-results'
                  role='tabpanel'
                  aria-labelledby='presettlement-results-tab'
                  hidden={sectionsVisible}
                >
                  <Card shadow='sm'>
                    <CardBody className='p-6'>
                      {/* Filters Section */}
                      <p className='font-bold text-lg mb-4'>{t('Filtros')}</p>
                      <div className='flex flex-col md:flex-row gap-4 mb-4'>
                        <SeasonFilter size='md' />

                        <div className='flex flex-col md:flex-row gap-4 w-full'>
                          <CustomAutocomplete
                            label={t('Grupo Productor')}
                            placeholder={t('Buscar...')}
                            options={producerGroupOptions}
                            selectedKey={selectedProducerGroup || undefined}
                            onSelectionChange={(key) =>
                              setSelectedProducerGroup(key ? String(key) : null)
                            }
                            className='w-full'
                          />

                          <CustomAutocomplete
                            label={t('Productor')}
                            placeholder={t('Buscar...')}
                            options={producerOptions}
                            selectedKey={selectedProducer || undefined}
                            onSelectionChange={(key) =>
                              setSelectedProducer(key ? String(key) : null)
                            }
                            className='w-full'
                          />

                          <CustomAutocomplete
                            label={t('Variedad')}
                            placeholder={t('Buscar...')}
                            options={varietyOptions}
                            selectedKey={selectedVariety || undefined}
                            onSelectionChange={(key) =>
                              setSelectedVariety(key ? String(key) : null)
                            }
                            className='w-full'
                          />

                          <div className='flex items-end w-full'>
                            <Button
                              variant='flat'
                              className='w-full'
                              onClick={() => {
                                setSelectedProducerGroup(null);
                                setSelectedProducer(null);
                                setSelectedVariety(null);
                              }}
                            >
                              {t('Limpiar filtros')}
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className='flex flex-col gap-4 mb-4'>
                        <p className='font-bold text-lg'>
                          {t('Visualización')}
                        </p>
                        <div className='flex flex-col md:flex-row gap-4 mb-4'>
                          <Select
                            label={t('Por Productor o Variedad')}
                            selectedKeys={[viewMode]}
                            onChange={(e) =>
                              setViewMode(
                                e.target.value as 'variety' | 'producer'
                              )
                            }
                            className='md:max-w-[200px]'
                          >
                            <SelectItem key='producer' value='producer'>
                              {t('Por Productor')}
                            </SelectItem>

                            <SelectItem key='variety' value='variety'>
                              {t('Por Variedad')}
                            </SelectItem>
                          </Select>
                          <Select
                            label={t('Agrupado o Por Calibre')}
                            selectedKeys={[
                              isCollapsed ? 'collapsed' : 'detailed',
                            ]}
                            onChange={(e) =>
                              setIsCollapsed(e.target.value === 'collapsed')
                            }
                            className='md:max-w-[200px]'
                          >
                            <SelectItem key='collapsed' value='collapsed'>
                              {t('Agrupado')}
                            </SelectItem>
                            <SelectItem key='detailed' value='detailed'>
                              {t('Por Calibre')}
                            </SelectItem>
                          </Select>
                        </div>
                      </div>

                      {/* Table Section */}
                      {!groupBy ? (
                        <div className='flex justify-center items-center h-[200px]'>
                          <p className='text-danger text-sm font-medium'>
                            {t(
                              'Debe seleccionar un criterio de cálculo (Pool de Liquidación) para ver la preliquidación.'
                            )}
                          </p>
                        </div>
                      ) : loading ? (
                        <LoadingState />
                      ) : (
                        <PreSettlementsTable
                          data={processedData}
                          producerData={processedProducerData}
                          movements={processedMovements}
                          calibers={isCollapsed ? ['average'] : [...order]}
                          groupBy={groupBy}
                          viewMode={viewMode}
                          showAdvanceColumns={exporterUsesFrutic(
                            userProfile?.exporter,
                            selectedSeason
                          )}
                        />
                      )}
                    </CardBody>
                  </Card>
                </div>
              </>
            )}
          </>
        )}

        {!shouldBlockPreSettlements &&
          settingsReady &&
          hasCalculationChanges && (
            <div className='fixed bottom-20 sm:bottom-6 left-1/2 z-40 w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 rounded-2xl border border-primary/30 bg-content1/95 p-4 shadow-2xl backdrop-blur-md'>
              <div className='flex flex-wrap items-center justify-between gap-3'>
                <div role='status' className='flex items-center gap-3'>
                  <span
                    aria-hidden='true'
                    className='h-2.5 w-2.5 shrink-0 rounded-full bg-primary'
                  />
                  <div>
                    <p className='text-sm font-semibold'>
                      {t('Cambios sin guardar')}
                    </p>
                    <p className='text-xs text-default-500'>
                      {t('Modificaste las variables de cálculo.')}
                    </p>
                  </div>
                </div>
                <div className='flex flex-wrap items-center justify-end gap-3'>
                  <Button
                    color='primary'
                    variant='solid'
                    className='min-w-[220px] font-semibold shadow-sm'
                    startContent={<Icon icon='tabler:device-floppy' />}
                    onClick={onSaveModalOpen}
                    isLoading={savingSettings}
                    isDisabled={
                      !userProfile?.exporter_id ||
                      !selectedSeason ||
                      !selectedSpecies?.id ||
                      !groupBy ||
                      loading ||
                      loadingSettings ||
                      savingSettings ||
                      !tariffsValid ||
                      !growerExceptionsValid
                    }
                  >
                    <span className='whitespace-nowrap'>
                      {t('Guardar Variables de Cálculo')}
                    </span>
                  </Button>
                </div>
              </div>
              {settingsSaveError && (
                <p role='alert' className='mt-2 text-sm text-danger'>
                  {t('No se pudieron guardar los cambios. Intenta nuevamente.')}
                </p>
              )}
            </div>
          )}

        {/* Save Settings Modal */}
        <Modal isOpen={isSaveModalOpen} onOpenChange={onSaveModalOpenChange}>
          <ModalContent>
            {(onClose) => (
              <>
                <ModalHeader className='flex flex-col gap-1'>
                  {t('Guardar Configuración de Preliquidación')}
                </ModalHeader>
                <ModalBody>
                  <p>
                    {t(
                      '¿Deseas guardar la configuración actual para esta temporada y especie?',
                      { season: selectedSeason, species: selectedSpecies?.name }
                    )}
                  </p>
                  <p className='text-sm text-default-500'>
                    {t('Esto incluye:')}
                  </p>
                  <ul className='text-sm text-default-500 list-disc list-inside ml-4'>
                    <li>
                      {t('Criterio de Cálculo')}:{' '}
                      {groupBy === 'reception'
                        ? t('Semana Recepción')
                        : groupBy === 'process'
                          ? t('Semana Proceso')
                          : groupBy === 'packing'
                            ? t('Semana Despacho Packing')
                            : groupBy === 'ship'
                              ? t('Nave (Legacy)')
                              : groupBy === 'reference'
                                ? t('Referencia (Legacy)')
                                : t('Sin seleccionar')}
                    </li>
                    <li>
                      {t('Variables Para Liquidar')}:{' '}
                      {productDefinition.join(', ')}
                    </li>
                    <li>
                      {t('Aplicación de Tarifas')}: {tariffApplicationLabel} ·{' '}
                      {tariffSummary}
                    </li>
                    <li>
                      {t('Porcentaje Comisión Exportadora')}: {percentageTake}%
                    </li>
                    <li>
                      {t('Tarifa general')}: {tariffConfig.defaultRate} USD/kg
                    </li>
                    <li>
                      {t('Excepciones por productor')}:{' '}
                      {growerExceptions.length}
                    </li>
                  </ul>
                  {settingsSaveError && (
                    <p role='alert' className='text-sm text-danger'>
                      {t(
                        'No se pudieron guardar los cambios. Intenta nuevamente.'
                      )}
                    </p>
                  )}
                  <p className='text-sm text-default-400'>
                    {t(
                      'La próxima vez que ingreses a esta temporada y especie, se aplicará automáticamente esta configuración.'
                    )}
                  </p>
                </ModalBody>
                <ModalFooter>
                  <Button variant='light' onPress={onClose}>
                    {t('Cancelar')}
                  </Button>
                  <Button
                    color='primary'
                    onPress={handleSaveSettings}
                    isLoading={savingSettings}
                    isDisabled={
                      loading ||
                      !settingsReady ||
                      !tariffsValid ||
                      !growerExceptionsValid
                    }
                  >
                    {savingSettings ? t('Guardando...') : t('Guardar')}
                  </Button>
                </ModalFooter>
              </>
            )}
          </ModalContent>
        </Modal>
      </PageLayout>
    </div>
  );
};

export default PreSettlementsPageNew;
